import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import type { FunnelStage } from '../types';
import { formatNumber, formatBRL } from '../lib/format';

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '12px',
      fontFamily: 'var(--font-mono)',
    }}>
      <div style={{ color: 'var(--text-secondary)', marginBottom: '4px', fontFamily: 'var(--font-sans)' }}>{label}</div>
      <div style={{ color: 'var(--text-primary)' }}>{formatNumber(payload[0].value)} leads</div>
    </div>
  );
}

interface Props {
  stages: FunnelStage[];
  color?: string;
}

export function FunnelChart({ stages, color = 'var(--service-1)' }: Props) {
  if (stages.length === 0) {
    return (
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        fontSize: '13px',
      }}>
        Nenhum dado de funil disponível
      </div>
    );
  }

  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      padding: '20px',
    }}>
      <ResponsiveContainer width="100%" height={Math.max(stages.length * 44, 120)}>
        <BarChart
          layout="vertical"
          data={stages}
          margin={{ top: 0, right: 80, bottom: 0, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, maxCount]}
            tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'var(--text-tertiary)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="status_name"
            width={160}
            tick={{ fontSize: 11, fontFamily: 'var(--font-sans)', fill: 'var(--text-secondary)' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]} label={{
            position: 'right',
            formatter: (v: number) => formatNumber(v),
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            fill: 'var(--text-secondary)',
          }}>
            {stages.map((_, i) => (
              <Cell
                key={i}
                fill={color}
                fillOpacity={1 - (i / stages.length) * 0.5}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function FunnelValueTable({ stages }: { stages: FunnelStage[] }) {
  if (stages.length === 0) return null;
  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      overflow: 'hidden',
      marginTop: '16px',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            {['Etapa', 'Leads', 'Valor'].map(h => (
              <th key={h} style={{
                padding: '10px 16px',
                textAlign: h === 'Etapa' ? 'left' : 'right',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--text-tertiary)',
                fontFamily: 'var(--font-sans)',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stages.map((s, i) => (
            <tr key={s.status_id} style={{
              borderBottom: i < stages.length - 1 ? '1px solid var(--border-subtle)' : 'none',
            }}>
              <td style={{ padding: '10px 16px', fontSize: '13px', color: 'var(--text-primary)' }}>
                {s.status_name}
              </td>
              <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                {formatNumber(s.count)}
              </td>
              <td style={{ padding: '10px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                {formatBRL(s.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
